import { registerHttpRequest } from '@/services/api/http.service';
import { authService } from '@/services/auth.service';
import {
  createCameraGroup,
  deleteCameraGroup,
  getCameraGroupList,
  getCameraRoles,
  getDirectoryGroup,
  updateDirectoryGroup,
  updateCameraGroup,
  updateCameraGroupSetting,
  updateCameraRoles,
} from '@/services/ccs.group.service';
import { getInvitedUsers, getUserAvatarList } from '@/services/ccs.user.servic';
import {
  getCamImageSettings,
  getCamRecordSettings,
  oneshotfocus,
  rebootCam,
  updateDaynightSetting,
  updateDisplaytimeSetting,
  updateFirmware,
  updateRecordingAudioSetting,
  updateRecordingVideoSetting,
  updateWdrSetting,
  getCameraMotion,
  saveCameraMotion,
  disableCameraMotion,
  updatePowerFrequency,
} from '@/services/settings.service';
import { registerInvitedUser, removeAvatar } from '@/services/user.service';
import { videoService } from '@/services/video.service';
import { getCurrentAvatarId, saveCurrentAvatarId } from '@/utils/user';
import Vue from 'vue';
import CameraService from '@/services/CameraService';
import StorageService from '@/services/StorageService';
import { FetchCamRecordInfoKeys } from '@/globals/enums';
import { sortObjectsByIds } from '@/utils/helpers';
import cloneDeep from 'clone-deep';

const state = {
  imgId: null,
  loggedIn: false,
  isCurrentAvatarOwner: null,
  currentAvatarId: null,
  currentCameraDirectoryName: '',
  userAvatars: [],
  currentCameraDirectoryId: null,
  newUserCamList: [],
  currentAvatars: null,
  avatarOwnerIds: [],
  users: [],
  avatarName: '',
  currentSelectedCamsGroup: {},
  groupCams: [],
  groupCamsChecklist: [],
  currentAvatarCameraRoles: null,
  camWithGroups: {},
  camRecordInfo: {},
  noticeSettings: {
    push: [false, false, false, false],
    mail: [false, false, false, false],
    target: [],
    groups: [],
  },
  noticeSettingsLoading: false,
  registeredCams: [
    {
      id: 'id1',
      thumbnailUrl: '/img/events/1.png',
      camName: '名称未設定のカメラ 2',
      camPropName: 'ブックカフェシニセ 六本木店',
      modelName: 'VIVOTEK FD9166-HN',
      serialNumber: 'XX-XX-XX-XX-XX-XX',
      firmwareVersion: '2.2002.13.01j',
      cameraId: 'camId',
      packageVersion: 'XXX-0.00',
      status: 'status',
      tags: [],
    },
    {
      id: 'id2',
      thumbnailUrl: '/img/events/2.png',
      camName: '名称未設定のカメラ 1',
      camPropName: 'ブックカフェシニセ 六本木店',
      modelName: 'VIVOTEK FD9166-HN',
      serialNumber: 'XX-XX-XX-XX-XX-XX',
      firmwareVersion: '2.2002.13.01j',
      cameraId: 'camId',
      packageVersion: 'XXX-0.00',
      status: 'status',
      tags: [],
    },
    {
      id: 'id3',
      thumbnailUrl: '/img/events/3.png',
      camName: '老舗書店 銀座支店 正面出入口',
      camPropName: '老舗書店 銀座支店',
      modelName: 'VIVOTEK FD9166-HN',
      serialNumber: 'XX-XX-XX-XX-XX-XX',
      firmwareVersion: '2.2002.13.01j',
      cameraId: 'camId',
      packageVersion: 'XXX-0.00',
      status: 'status',
      tags: [],
    },
  ],
};

// Getter functions
const getters = {
  usersWithOwner(state) {
    return state.users
      ? state.users.map((u) => {
          return {
            ...u,
            owner: state.avatarOwnerIds.includes(u.id),
          };
        })
      : [];
  },
  getImgId() {
    return state.loggedIn;
  },
  getLoggedIn() {
    return state.loggedIn;
  },
};

// Mutations
const mutations = {
  setImgId(state, data) {
    state.imgId = data;
  },
  setLoggedIn(state, data) {
    state.loggedIn = data;
  },
  setUsers(state, data) {
    state.users = data;
  },
  setCurrentAvatarId(state, currentAvatarId) {
    state.currentAvatarId = currentAvatarId;
  },
  setCurrentCameraDirectoryName(state, name) {
    state.currentCameraDirectoryName = name;
  },
  setIsCurrentAvatarOwner(state, isOwner) {
    state.isCurrentAvatarOwner = isOwner;
  },
  setUserAvatars(state, avatars) {
    state.userAvatars = avatars;
  },
  setCurrentCameraDirectoryId(state, currentCameraDirectoryId) {
    state.currentCameraDirectoryId = currentCameraDirectoryId;
  },
  setCurrentSelectedCamsGroup(state, currentSelectedCamsGroup) {
    state.currentSelectedCamsGroup = currentSelectedCamsGroup;
  },
  setAvatarName(state, avatarName) {
    state.avatarName = avatarName;
  },
  setCurrentAvatars(state, payload) {
    state.currentAvatars = payload;
  },
  setGroupCams(state, groups) {
    state.groupCams = groups;
  },
  setCamWithGroups(state, groups) {
    state.camWithGroups = groups;
  },
  setCurrentAvatarCameraRoles(state, roles) {
    state.currentAvatarCameraRoles = roles;
  },
  setGroupCamsChecklist(state, groups) {
    state.groupCamsChecklist = groups;
  },
  setNewUserCamList(state, payload) {
    state.newUserCamList = payload;
  },
  setCamRecordInfo(state, payload) {
    const { cameraId, result } = payload;
    Vue.set(state.camRecordInfo, cameraId, result);
  },
  setNoticeSetting(state, payload) {
    Vue.set(state.noticeSettings, payload.key, payload.value);
  },
  setNoticeSettingsLoading(state, payload) {
    state.noticeSettingsLoading = payload;
  },
  initNoticeSetting(state, payload) {
    state.noticeSettings = payload;
  },
  setAvatarOwnerIds(state, payload) {
    state.avatarOwnerIds = payload;
  },
  clearAll(state) {
    state.imgId = null;
    state.loggedIn = false;
    state.isCurrentAvatarOwner = null;
    state.currentAvatarId = null;
    state.currentCameraDirectoryName = '';
    state.userAvatars = [];
    state.currentCameraDirectoryId = null;
    state.newUserCamList = [];
    state.currentAvatars = null;
    state.avatarOwnerIds = [];
    state.users = [];
    state.avatarName = '';
    state.currentSelectedCamsGroup = {};
    state.groupCams = [];
    state.groupCamsChecklist = [];
    state.currentAvatarCameraRoles = null;
    state.camWithGroups = {};
    state.camRecordInfo = {};
    state.noticeSettings = {
      push: [false, false, false, false],
      mail: [false, false, false, false],
      target: [],
      groups: [],
    };
    state.noticeSettingsLoading = false;
  },
  updateCameraGroupOrder(state, payload) {
    const { groupId, ids } = payload;
    const groupIndex = state.groupCams.findIndex((g) => g.id === groupId);

    if (groupIndex >= 0) {
      // sort all cameras in the group based on the ids order provided in the payload
      const camGroupDetails = sortObjectsByIds(
        ids,
        state.groupCams[groupIndex].details
      );
      const groupCamsChecklistDetails = sortObjectsByIds(
        ids,
        state.groupCamsChecklist[groupIndex].details,
        false
      );

      // clear all cameras in the group
      Vue.set(state.groupCams[groupIndex], 'details', []);
      Vue.set(state.groupCamsChecklist[groupIndex], 'details', []);

      // resort all cameras in the group
      camGroupDetails.forEach((c) => {
        state.groupCams[groupIndex].details.push({ ...c });
      });
      groupCamsChecklistDetails.forEach((c) => {
        state.groupCamsChecklist[groupIndex].details.push({ ...c });
      });
    }
  },
};

// Actions
const actions = {
  changeImgId({ commit }, data) {
    commit('setImgId', data);
  },
  changeLoggedIn({ commit }, data) {
    commit('setLoggedIn', data);
  },
  async fetchInvitedUsers({ commit, dispatch, rootState }) {
    try {
      const result = await getInvitedUsers();
      const storedAvatarId = getCurrentAvatarId();
      const avatarExistsInResult =
        storedAvatarId &&
        result.avatars.some((avatar) => avatar.id === Number(storedAvatarId));
      const currentAvatarId = avatarExistsInResult
        ? storedAvatarId
        : result.currentAvatarId;
      const roles = await getCameraRoles({
        avatarIds: [currentAvatarId],
      });
      commit('setCurrentAvatarCameraRoles', roles.avatarRoleLists[0]?.policies);
      commit(
        'setAvatarOwnerIds',
        result.avatars.filter((a) => a.owner === 1).map((a) => a.id)
      );
      commit('setCurrentAvatarId', currentAvatarId);

      const currentAvatar = result.avatars.find(
        (avatar) => avatar.id === Number(currentAvatarId)
      );

      if (!currentAvatar) {
        console.error('fetchInvitedUsers: Avatar not found', {
          currentAvatarId,
          availableAvatars: result.avatars.map((a) => ({
            id: a.id,
            name: a.name,
          })),
        });
        return;
      }

      StorageService.updateRecentAvatars(currentAvatarId);

      commit('setAvatarName', currentAvatar.name);
      commit('setCurrentCameraDirectoryId', currentAvatar.cameraDirectoryId);
      commit('setUserAvatars', result.avatars);
      commit(
        'setCurrentCameraDirectoryName',
        currentAvatar.cameraDirectoryName
      );
      commit('setIsCurrentAvatarOwner', currentAvatar.owner === 1);
      dispatch(
        'common/setCurrentCameraGroupRole',
        rootState.common.currentHomeGroup,
        { root: true }
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async registerInvitedUser(_, payload) {
    return registerInvitedUser(payload);
  },
  async updateFirmware({ dispatch, rootState }, payload) {
    const requests = [];
    Object.keys(payload).forEach((name) => {
      const cameraIds = payload[name].map((cam) => cam.id);
      if (cameraIds && cameraIds.length) {
        requests.push(
          updateFirmware({
            cameraIds: payload[name].map((cam) => cam.id),
            name,
          })
        );
      }
    });
    const result = await Promise.allSettled(requests);
    const successData = [];
    result.forEach((res) => {
      if (res.status === 'fulfilled') {
        res.value.results.forEach((data) => {
          successData.push(data);
          if (data.code === 0) {
            const versions = [
              ...rootState.common.cameraById[data.cameraId].UpdateVersion,
            ];
            versions[0].updatingState = 1;
            dispatch(
              'common/updateCamera',
              {
                id: data.cameraId,
                UpdateVersion: versions,
              },
              { root: true }
            );
          }
        });
      }
    });
    return successData;
  },
  async updateFirmwareDetail({ dispatch, rootState }, payload) {
    const res = await updateFirmware(payload);
    if (res && res.results && res.results[0]) {
      const result = res.results[0];
      if (result.code === 0) {
        const firmwareUpdateIndex = rootState.common.cameraById[
          result.cameraId
        ].UpdateVersion.findIndex((u) => u.name === payload.name);
        const versions = [
          ...rootState.common.cameraById[result.cameraId].UpdateVersion,
        ];
        versions[firmwareUpdateIndex].updatingState = 1;
        dispatch(
          'common/updateCamera',
          {
            id: result.cameraId,
            UpdateVersion: versions,
          },
          { root: true }
        );

        return true;
      }
    }
    return false;
  },
  async createCameraGroup(_, payload) {
    return createCameraGroup(payload);
  },
  async updateCameraGroupSetting(_, payload) {
    await updateCameraGroupSetting(payload);
  },
  async updateCameraGroup(_, payload) {
    await updateCameraGroup(payload);
  },
  async deleteCameraGroup(_, payload) {
    await deleteCameraGroup(payload);
  },
  async setAvatarName({ commit }, payload) {
    commit('setAvatarName', payload);
  },
  async editUserCameraGroup({ commit, state }, payload) {
    const result = await getCameraRoles({
      avatarIds: payload.avatarIds,
    });

    const role = result.avatarRoleLists.find(
      (role) => +role.avatarId === +payload.avatarIds[0]
    );

    let newUserCamList = [];
    if (role) {
      newUserCamList = role.policies.map((policy) => {
        let livePermissionSwitch = false;
        let cameraPolicyAccess = false;
        let cameraPolicyLiveOnly = false;
        if (policy.policyItems) {
          const access = policy.policyItems.find(
            (item) => item.name === 'CameraPolicy_Access'
          );
          const liveOnly = policy.policyItems.find(
            (item) => item.name === 'CameraPolicy_LiveOnly'
          );

          livePermissionSwitch = !!access && access.value === 1;
          cameraPolicyAccess = !!access && access.value === 1;
          cameraPolicyLiveOnly = !!liveOnly && liveOnly.value === 1;
        }
        const camGroup = state.groupCams.find(
          (cam) => String(cam.id) === String(policy.cameraGroupId)
        );
        return {
          ...camGroup,
          livePermissionSwitch,
          cameraPolicyAccess,
          cameraPolicyLiveOnly,
        };
      });
    }
    commit('setNewUserCamList', newUserCamList);
  },
  async deleteCameraGroupAvatar({ dispatch }, payload) {
    const { avatarId, groupId } = payload;
    const result = await getCameraRoles({
      avatarIds: [avatarId],
    });

    const role = result.avatarRoleLists.find(
      (role) => role.avatarId === avatarId
    );

    // map avatar camera group to delete
    const cameraGroupRoleToUpdate = {
      avatarId: avatarId,
      policies: [],
    };

    if (role) {
      role.policies.forEach((policy) => {
        if (policy.cameraGroupId !== groupId) {
          cameraGroupRoleToUpdate.policies.push(policy);
        }
      });
    }
    // Delete camera group from avatar
    await dispatch('updateCameraGroupRole', cameraGroupRoleToUpdate);
  },
  /**
   *  Map camera group
   *
   * */
  mapCameraGroup(
    { dispatch, commit, state, rootState },
    { tags, noSelectedGroupChange = false }
  ) {
    let groupCams = [];
    let groupCamsChecklist = [];
    const camWithGroups = {};
    if (tags) {
      tags.forEach((tag) => {
        const groupCam = {
          id: tag.id,
          title: tag.name,
          details: [],
        };
        const groupCamChecklist = {
          id: tag.id,
          title: tag.name,
          details: [],
        };
        rootState.common.cameraList.forEach((cam) => {
          // map cam with groups
          if (!camWithGroups[cam.id]) {
            camWithGroups[cam.id] = [];
          }
          const camDetail = {
            title: cam.name,
            id: cam.id,
            index: cam.index,
            active: false,
          };

          // map groups with cam
          if (
            cam.tagInfoList &&
            cam.tagInfoList.find((camTag) => camTag.id === tag.id)
          ) {
            camWithGroups[cam.id].push(tag);
            groupCam.details.push(camDetail);
            camDetail.active = true;
          }
          // push all items for check list view
          groupCamChecklist.details.push(camDetail);
        });

        // sort cameras by id
        groupCam.details = sortObjectsByIds(tag.cameraIds, groupCam.details);
        groupCamChecklist.details = sortObjectsByIds(
          tag.cameraIds,
          groupCamChecklist.details,
          false
        );

        // push group cam to list
        groupCams.push(groupCam);
        groupCamsChecklist.push(groupCamChecklist);
      });
    }

    let selectedHomeGroup = state.currentAvatarId
      ? CameraService.getSelectedHomeGroup(state.currentAvatarId)
      : null;

    const groupCamsWithCam = groupCams.filter(
      (g) =>
        !!rootState.common.homeCameraGroups[g.id] &&
        (state.isCurrentAvatarOwner === true || +g.id !== 0)
    );

    // when no camera groups available at all
    if (groupCamsWithCam.length === 0) {
      dispatch('common/noCameraFound', true, { root: true });
    } else {
      dispatch('common/noCameraFound', false, { root: true });
    }

    const getValidHomeGroup = (selectedHomeGroup, groupCamsWithCam) => {
      // if the stored group is not in the list, fallback to the first available group
      if (
        groupCamsWithCam.findIndex((g) => +g.id === +selectedHomeGroup) === -1
      ) {
        return groupCamsWithCam[0] ? groupCamsWithCam[0].id : null;
      }
      return selectedHomeGroup;
    };

    if (selectedHomeGroup === null) {
      // if no group is selected, fallback to the first available group
      selectedHomeGroup = groupCamsWithCam.length
        ? groupCamsWithCam[0].id
        : null;
    } else {
      // validate the selected group
      selectedHomeGroup = getValidHomeGroup(
        selectedHomeGroup,
        groupCamsWithCam
      );
    }

    if (!noSelectedGroupChange) {
      dispatch('common/setCurrentHomeGroup', selectedHomeGroup, { root: true });
    }

    commit('setGroupCams', cloneDeep(groupCams));
    commit('setCamWithGroups', cloneDeep(camWithGroups));
    commit('setGroupCamsChecklist', cloneDeep(groupCamsChecklist));
    dispatch('checkCameraListSettingLocal');
  },
  /**
   *  Fetch camera group
   *
   * */
  async fetchCameraGroup({ dispatch, rootState }, payload) {
    const call$ = [getCameraGroupList(payload)];
    if (
      !rootState.common.cameraList?.length &&
      !rootState.common.cameraListLoading
    ) {
      call$.push(videoService.getCameraList());
    }
    const [result] = await Promise.all(call$);
    dispatch('mapCameraGroup', {
      tags: result && result.tags ? result.tags : [],
    });
  },
  checkCameraListSettingLocal({ state }) {
    const savedCameraList = CameraService.getCameraListSetting(
      state.currentAvatarId,
      state.isCurrentAvatarOwner
    );
    if (savedCameraList && savedCameraList.length > 0) {
      const mappedData = state.groupCams.map((g, index) => {
        return {
          index,
          id: g.id,
          name: g.title,
          registerd: g.details.length,
          account: 100,
          selected: false,
        };
      });
      // Update objects in savedCameraList from data in mappedData by their ids
      for (const response of mappedData) {
        const localIndex = savedCameraList.findIndex(
          (local) => local.id === response.id
        );
        if (localIndex >= 0) {
          Object.assign(savedCameraList[localIndex], response);
        } else {
          savedCameraList.push(response);
        }
      }
      // Remove items from savedCameraList that are not in mappedData
      const responseIds = new Set(mappedData.map((response) => response.id));
      for (let i = savedCameraList.length - 1; i >= 0; i--) {
        const localItem = savedCameraList[i];
        if (!responseIds.has(localItem.id)) {
          savedCameraList.splice(i, 1);
        }
      }
      // Update local storage
      CameraService.saveCameraListSetting(
        savedCameraList,
        state.currentAvatarId,
        state.isCurrentAvatarOwner
      );
    }
  },
  setCurrentSelectedCamsGroup({ commit }, payload) {
    commit('setCurrentSelectedCamsGroup', payload);
  },
  setNewUserCamList({ commit }, payload) {
    commit('setNewUserCamList', payload);
  },
  async removeAvatar(_, avatarId) {
    await removeAvatar(avatarId);
  },
  async updateCameraGroupRole(_, payload) {
    await updateCameraRoles(payload);
  },
  async fetchUserAvatarList({ commit, dispatch, state }) {
    await dispatch('fetchInvitedUsers');
    const result = await getUserAvatarList({
      cameraDirectoryId: state.currentCameraDirectoryId,
    });
    const currentUserEmail = await authService.getCurrentUserEmail();
    const current = result.userAvatarLists.find(
      (user) => user.userAccessKey === currentUserEmail
    );
    let mappedUserAvatarList = [];
    if (result && result.userAvatarLists && result.userAvatarLists.length) {
      const usersAvatarIds = result.userAvatarLists
        .filter((item) => item.avatars.length > 0)
        .map((item) => ({
          userAccessKey: item.userAccessKey,
          ...(item.avatars.find(
            (item) => item?.cameraDirectoryId === state.currentCameraDirectoryId
          ) || {}),
        }))
        .map((avatar) => avatar.id);
      const roles = await getCameraRoles({
        avatarIds: usersAvatarIds,
      });
      let userGroupCams = [];
      if (roles && roles.avatarRoleLists && roles.avatarRoleLists.length) {
        const cameraGroupMap = state.groupCams.reduce((map, obj) => {
          map[obj.id] = obj.title;

          return map;
        }, {});

        // access to all cameras when camera group id is zero(0)
        cameraGroupMap[0] = '全て';

        roles.avatarRoleLists.forEach((item) => {
          userGroupCams.push({
            avatarId: item.avatarId,
            groupCamList: item.policies
              .map((obj) => ({
                ...obj,
                name: cameraGroupMap[obj.cameraGroupId],
              }))
              .map((groupCam) => groupCam.name)
              .join(' '),
            groupCamIds: item.policies.map(
              (groupCam) => groupCam.cameraGroupId
            ),
          });
        });
      }
      mappedUserAvatarList = result.userAvatarLists.map((item) => {
        return {
          email: item.userAccessKey,
          id: item.avatars[0].id,
          avatarIds: item.avatars.map((avatar) => avatar.id),
          name: item.avatars[0].name,
          owner: state.avatarOwnerIds.includes(item.avatars[0].id),
          groupCams: userGroupCams.find(
            (group) => group.avatarId === item.avatars[0].id
          ),
        };
      });
    }
    commit('setCurrentAvatars', current);
    commit('setUsers', mappedUserAvatarList);
  },
  clearAll({ commit }) {
    commit('clearAll');
  },
  async autofocus(_, cameraId) {
    await oneshotfocus(cameraId);
  },
  async fetchCamRecordInfo({ commit }, { cameraId, reqId }) {
    const recordParams = {};
    Object.setPrototypeOf(recordParams, {
      __reqId: `${FetchCamRecordInfoKeys.getCamRecordSettings_}${reqId}`,
    });

    const imageParams = {};
    Object.setPrototypeOf(imageParams, {
      __reqId: `${FetchCamRecordInfoKeys.getCamImageSettings_}${reqId}`,
    });

    const motionParams = {};
    Object.setPrototypeOf(motionParams, {
      __reqId: `${FetchCamRecordInfoKeys.getCameraMotion_}${reqId}`,
    });

    const [recordSettingsData, imageSettingsData, motionSettingsData] =
      await Promise.allSettled([
        getCamRecordSettings(cameraId, recordParams),
        getCamImageSettings(cameraId, imageParams),
        getCameraMotion(cameraId, motionParams),
      ]);

    let recordSettings = {
      video: 0,
      audio: 0,
    };

    let imageSettings = {
      displaytime: 0,
      oneshotfocus: 0,
      daynight: 'AUTO',
      wdr: 'OFF',
      irled: 'OFF',
      powerfrequency: 'NA',
    };

    let motionSettings = null;

    if (recordSettingsData.status === 'fulfilled') {
      recordSettings = recordSettingsData.value;
      recordSettings.powerfrequency =
        recordSettings.powerfrequency === undefined
          ? 'NA'
          : recordSettings.powerfrequency;
    } else {
      console.error(recordSettingsData.reason);
    }
    if (
      imageSettingsData.status === 'fulfilled' &&
      imageSettingsData.value.daynight !== undefined
    ) {
      imageSettings = imageSettingsData.value;
    } else {
      console.error(imageSettingsData.reason);
    }

    if (motionSettingsData.status === 'fulfilled') {
      motionSettings = motionSettingsData.value;
    } else {
      console.error(motionSettingsData.reason);
    }

    const result = {
      ...recordSettings,
      ...imageSettings,
      ...{ motionSettings },
    };
    commit('setCamRecordInfo', { cameraId, result });
  },
  async updateRecordSettings({ state, commit }, payload) {
    const { cameraId, data } = payload;
    let res = {};
    if (data.video !== undefined) {
      const payload = {
        value: data.video,
      };

      registerHttpRequest('updateRecordingVideoSetting', payload);

      res = await updateRecordingVideoSetting(cameraId, payload);
    } else if (data.audio !== undefined) {
      const payload = {
        value: data.audio,
      };

      registerHttpRequest('updateRecordingAudioSetting', payload);

      res = await updateRecordingAudioSetting(cameraId, payload);
    } else if (data.displaytime !== undefined) {
      const payload = {
        value: data.displaytime,
      };

      registerHttpRequest('updateDisplaytimeSetting', payload);
      res = await updateDisplaytimeSetting(cameraId, payload);
    } else if (data.wdr !== undefined) {
      const payload = {
        value: data.wdr,
      };

      registerHttpRequest('updateWdrSetting', payload);
      res = await updateWdrSetting(cameraId, payload);
    } else if (data.daynight) {
      const payload = {
        value: data.daynight,
      };

      registerHttpRequest('updateDaynightSetting', payload);
      res = await updateDaynightSetting(cameraId, payload);
    } else if (data.powerfrequency) {
      const payload = {
        value: data.powerfrequency,
      };

      registerHttpRequest('updatePowerFrequencySetting', payload);
      res = await updatePowerFrequency(cameraId, payload);
    }
    if (!res._isCancel) {
      commit('setCamRecordInfo', {
        result: { ...state.camRecordInfo[cameraId], ...res },
        cameraId,
      });
    }
  },
  async rebootCamera(_, cameraId) {
    await rebootCam(cameraId);
  },
  changeCurrentAvatarId(
    { commit, state },
    { id, updateRecentAvatars = false }
  ) {
    const oldAvatarId = state.currentAvatarId;

    if (oldAvatarId && oldAvatarId !== id) {
      StorageService.clearOnAvatarSwitch(id);
    }

    if (updateRecentAvatars) {
      saveCurrentAvatarId(id);
    }

    StorageService.ensureRecentAvatarsPopulated();

    commit('setCurrentAvatarId', id);
    const currentAvatar = state.userAvatars.find(
      (avatar) => avatar.id === Number(id)
    );
    commit('setAvatarName', currentAvatar.name);
    commit('setCurrentCameraDirectoryId', currentAvatar.cameraDirectoryId);
    commit('setCurrentCameraDirectoryName', currentAvatar.cameraDirectoryName);
    commit('setIsCurrentAvatarOwner', currentAvatar.owner === 1);
  },
  setNoticeSetting({ commit }, payload) {
    commit('setNoticeSetting', payload);
  },
  async fetchDirectoryGroup({ commit }) {
    try {
      commit('setNoticeSettingsLoading', true);

      const directoryGroup = await getDirectoryGroup();
      const cameraGroup = await getCameraGroupList();
      const noticeSettings = {
        push: [false, false, false, false],
        mail: [false, false, false, false],
        target: [],
        groups: [],
      };
      // Initilize camera groups for notice settings
      cameraGroup.tags.forEach((camGroup) => {
        noticeSettings.target.push(false);
        noticeSettings.groups.push(camGroup);
      });

      // Map data from policy if exist
      if (
        directoryGroup.cameraDirectoryRoleLists &&
        directoryGroup.cameraDirectoryRoleLists[0] &&
        directoryGroup.cameraDirectoryRoleLists[0].policies
      ) {
        directoryGroup.cameraDirectoryRoleLists[0].policies.forEach((p) => {
          let mapped = {};
          let items = {};
          if (p.policyItems) {
            mapped = p.policyItems.map((item) => ({
              [item.name]: item.value,
            }));
            items = Object.assign({}, ...mapped);
          }
          const index = cameraGroup.tags.findIndex(
            (g) => g.id === p.cameraGroupId
          );
          noticeSettings.target[index] = true;

          // PushNotification
          noticeSettings.push[0] = !!items['PushNotification'];
          noticeSettings.push[1] = !!items['PushNotification_Event'];
          noticeSettings.push[2] = !!items['PushNotification_CameraError'];
          noticeSettings.push[3] = !!items['PushNotification_VideoClip'];

          // MailNotification
          noticeSettings.mail[0] = !!items['MailNotification'];
          noticeSettings.mail[1] = !!items['MailNotification_Event'];
          noticeSettings.mail[2] = !!items['MailNotification_CameraError'];
          noticeSettings.mail[3] = !!items['MailNotification_VideoClip'];
        });
      }
      commit('initNoticeSetting', noticeSettings);
    } catch (error) {
      console.error(error);
    } finally {
      commit('setNoticeSettingsLoading', false);
    }
  },
  async updateNoticeSettings({ state, commit }) {
    try {
      commit('setNoticeSettingsLoading', true);
      const cameraDirectoryId = state.currentCameraDirectoryId;
      const { groups, target, push, mail } = state.noticeSettings;
      const policies = [];

      groups.forEach((g, index) => {
        if (target[index]) {
          const cameraGroupId = g.id;
          const policyItems = [
            {
              name: 'PushNotification',
              value: target[index] && push[0] ? 1 : 0,
            },
            {
              name: 'PushNotification_Event',
              value: push[1] && push[0] ? 1 : 0,
            },
            {
              name: 'PushNotification_CameraError',
              value: push[2] && push[0] ? 1 : 0,
            },
            {
              name: 'PushNotification_VideoClip',
              value: push[3] && push[0] ? 1 : 0,
            },
            {
              name: 'MailNotification',
              value: target[index] && mail[0] ? 1 : 0,
            },
            {
              name: 'MailNotification_Event',
              value: mail[1] && mail[0] ? 1 : 0,
            },
            {
              name: 'MailNotification_CameraError',
              value: mail[2] && mail[0] ? 1 : 0,
            },
            {
              name: 'MailNotification_VideoClip',
              value: mail[3] && mail[0] ? 1 : 0,
            },
          ];
          policies.push({
            cameraGroupId,
            policyItems,
          });
        }
      });
      const directory = {
        cameraDirectoryId,
        policies,
      };

      await updateDirectoryGroup(directory);
    } catch (error) {
      console.error(error);
    } finally {
      commit('setNoticeSettingsLoading', false);
    }
  },
  updateGroupCamById({ state, commit }, payload) {
    const { id, name } = payload;
    const updatedGroupCams = state.groupCams.map((item) => {
      if (+item.id === +id) {
        return { ...item, title: name };
      }
      return item;
    });
    commit('setGroupCams', updatedGroupCams);
  },
  async disableCameraMotion(_, cameraId) {
    await disableCameraMotion(cameraId);
  },
  async saveCameraMotion(_, data) {
    const cameraId = data.cameraId;
    const areaList = data.areaList.map((area, index) => ({
      id: area.id || index,
      name: area.name,
      polygonstd: area.polygonstd.join(','),
    }));
    const payload = {
      value: {
        enable: 1,
        sensitivity: data.sensitivity,
        rois: areaList,
      },
    };
    await saveCameraMotion(cameraId, payload);
  },
  updateCameraGroupOrder({ commit }, payload) {
    commit('updateCameraGroupOrder', payload);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
